/**
 * Thrivia Embed SDK: the checkout overlay, the popup fallback, and the degraded link.
 *
 * Purpose: get a visitor from a click on somebody else's website into Thrivia checkout, on every
 * host, without ever leaving them looking at nothing.
 *
 * THREE ROUTES, and every click takes exactly one of them:
 *
 * 1. **Overlay** (the normal web, Squarespace, Webflow, WordPress): a full-viewport fixed container
 *    holding an iframe of `/embed/checkout?event=<id>&sid=<sid>`. Focus trapped, ESC and backdrop
 *    close, body scroll locked and restored to the exact previous value.
 * 2. **Popup** (`platform.sandboxed`, which is Wix's HTML component and any sandboxed iframe): an
 *    overlay drawn inside a fixed-height cross-origin box is unusable, so a top-level window opens
 *    instead. It MUST be opened synchronously inside the click handler; every popup blocker on the
 *    market kills a `window.open` that happens after an await, and this file therefore contains no
 *    `await` and no promise anywhere on the path from click to open.
 * 3. **The degraded link** (R19): when a required `window.open` returns null the visitor gets a
 *    visible "Continue on thrivia.io" link they can click themselves, which carries their own
 *    activation and is never blocked. A silent stall is the one outcome that is not allowed: it
 *    looks like a broken website and it is the organizer who gets blamed for it.
 *
 * THE FRAMED ROUTE MAY NOT ANSWER. In production `/embed/checkout` can 404, be blocked by an
 * extension, be refused by an enterprise proxy, or, most often now, be refused by the browser itself
 * because the per-key `frame-ancestors` does not name this organizer's origin. A cross-origin iframe
 * reports none of that: `onload` fires for a refused frame exactly as it does for a real one, and
 * its document is unreadable either way. The only honest signal is the bus, so the frame is required
 * to SPEAK. Any valid bus message (the checkout page posts `resize` on its first paint) marks it
 * alive and cancels the liveness timer; silence past {@link FRAME_TIMEOUT_MS} means the route is
 * dead, and the visitor is TOLD so, in place, with a link out and a line naming the origin whoever
 * runs the site has to register. See {@link refused}.
 *
 * Reuses `render/card.ts`'s `e()` and the shipped stylesheet rather than growing a second DOM
 * helper or a second set of classes: one element factory is one XSS surface, and the fallback panel
 * is built from the card's own `.thrivia-c` box so it costs no new CSS at all.
 *
 * No runtime dependencies, DOM only, ES2019 output target.
 */

import { emitBooking, onBus, post, type BusPayload, type BusType } from "./bus";
import { C, OWN, e, linkTo } from "./render/card";
import { injectStyles } from "./styles";

/**
 * How long a framed surface has to say anything at all before it is presumed dead.
 *
 * FOUR SECONDS, DOWN FROM EIGHT, and the reason the number moved is that the most likely cause of
 * the silence changed. Eight was budgeted for a cold Next route on a slow connection, when a dead
 * frame meant a 404, an extension or an enterprise proxy: rare, so the wait was rarely paid. It is
 * now also what a browser does when the per-key `frame-ancestors` does not name this organizer's
 * origin, which is the ordinary state of an unregistered site and is refused INSTANTLY, before any
 * network. So the wait is now paid by real visitors on working websites, every click, and eight
 * seconds of blank white is longer than any of them will sit through.
 *
 * Shared with the `page` surface (`render/page.ts`), which had no liveness contract at all and left
 * a permanent empty box. One frame timeout, one decision.
 */
export const FRAME_TIMEOUT_MS = 4000;

/** A real popup window, not a tab: the checkout is a task, and a task wants its own frame. */
const POPUP = "width=480,height=760";

/**
 * The one degraded message, for both the blocked popup and the unreachable frame.
 *
 * Deliberately one string and not two. The visitor's next action is identical either way (click the
 * link), the diagnosis is ours rather than theirs, and a second sentence of copy is real bytes on
 * every organizer's page for a distinction only we can act on.
 */
const DEGRADED = "Checkout could not be opened here.";

/** The link out of every panel below. One string, used twice. */
const CONTINUE = "Continue on thrivia.io";

/**
 * The framing-refusal copy, and it is written for TWO readers at once because both of them see it.
 *
 * A member of the public may be standing in front of this, so it names nothing internal: no key, no
 * `frame-ancestors`, no CSP, no talk of learned or registered origins. What a visitor takes from it
 * is "this bit of the page is not set up yet, here is the link I actually wanted", and the link is a
 * real anchor carrying their own click.
 *
 * The second reader is the organizer, and for them the message has to be ACTIONABLE, which means it
 * has to carry the one string they need and cannot easily guess: the exact origin to register. So
 * the middle line is `location.origin` verbatim, sitting in the page, selectable and copyable. An
 * origin is scheme, host and port and nothing else, so nothing private travels in it.
 *
 * "Site owner:" is doing real work as a prefix. The instruction is addressed rather than asserted,
 * which is what keeps the sentence honest: a dead frame is USUALLY an unregistered domain now, but
 * it is not a proof (a 404, an extension or a broken network reach the same place), so the copy
 * offers the fix to the one person who can apply it instead of diagnosing at the visitor.
 */
const REFUSED = "Thrivia could not load here";
const OWNER = "Site owner: add ";
const DASHBOARD = " in your Thrivia dashboard.";

export interface CheckoutTarget {
  /** OUR origin, from the loader's own script tag. The frame, the bus and the popup all use it. */
  origin: string;
  /**
   * The snippet's publishable key, and it is NOT optional decoration.
   *
   * Task 7's `/embed/checkout` computes its `Content-Security-Policy: frame-ancestors` per request
   * from the key's registered domains, and falls back to same-origin only when it cannot find one,
   * which would mean the browser refuses to frame us on every organizer's website. The sid can
   * resolve to a key server-side, but the sid is legitimately null (an origin the key has not
   * learned yet, or its origin cap reached) and the frame still has to work in that state. The key
   * is already in the page source, in the snippet, so putting it in the frame URL leaks nothing.
   */
  key: string;
  eventId: string;
  /** Attribution session for the mount that was clicked, or null. Omitted from the URL when null. */
  sid: string | null;
  /**
   * Where to send the visitor when the frame cannot be used: `card.url` once data has arrived, and
   * before that the organizer's own baked permalink. Null only when the mount had neither.
   */
  fallback: string | null;
  /** `platform.sandboxed`: computed at boot, never during the click. */
  sandboxed: boolean;
  /**
   * `"brochure"` when the visitor pressed an itinerary-PDF control rather than Book now. The frame
   * then opens the brochure form instead of the booking flow, in the same overlay.
   */
  intent?: "brochure" | null;
}

/* The live overlay, if any. One at a time, always. */
let activeRoot: HTMLElement | null = null;
let activeClose: (() => void) | null = null;
let activeFrame: HTMLIFrameElement | null = null;
let activeTarget: CheckoutTarget | null = null;
/** The popup carrying checkout on a sandboxed host, where there is no frame to hold it. */
let checkoutWin: Window | null = null;
/** The login popup, once the frame has asked for one. */
let loginWin: Window | null = null;
let liveness: ReturnType<typeof setTimeout> | null = null;
let spoke = false;
/** The spinner over a frame that has not spoken yet; removed by the first bus message or by close. */
let spinner: HTMLElement | null = null;

function dropSpinner(): void {
  if (spinner) spinner.remove();
  spinner = null;
}
let subscribed = false;
/**
 * Takes the degraded login panel back off whichever frame it is covering, or null when none is.
 *
 * Module level and single, because a visitor can only be part way through one login at a time: the
 * panel is put up by the frame that asked for a popup, and the next thing that happens is either
 * that visitor clicking through it or the login completing. A map keyed by frame would be a second
 * structure to keep in step with a question that only ever has one answer.
 */
let uncover: (() => void) | null = null;

/** Whichever window is carrying checkout right now: the framed one, or the popup. */
function checkoutWindow(): Window | null {
  return activeFrame ? activeFrame.contentWindow : checkoutWin;
}

/**
 * Whether a message came from a document WE opened.
 *
 * The origin check in `bus.ts` is the security boundary and this is the narrower question on top of
 * it: same-origin is not the same as ours. Every `/embed/*` page answers from the app origin, so
 * from Task 7 the login popup is an app-origin document too, and so is any other embed page an
 * organizer happens to put in a frame of their own. Matching the window identity means a
 * `booking:complete` is only ever believed from the checkout we are actually running.
 *
 * A null source can never match: after a close every reference here is null, and `null === null`
 * would otherwise readmit exactly the stale post-close message this exists to stop.
 */
function known(source: unknown): boolean {
  return !!source && (source === checkoutWindow() || source === loginWin);
}

/**
 * The query tail every `/embed/*` URL this bundle opens carries: the key, the sid when there is
 * one, and the origin of THIS document.
 *
 * `parent_origin` is the one place in the whole loader that reads `window.location`, and it inverts
 * the rule the rest of the bundle follows for a reason. Everywhere else, reading `window.location`
 * would give us the ORGANIZER's origin where we wanted ours, which is exactly the bug `origin.ts`
 * exists to prevent. Here the organizer's origin IS the answer: a frame cannot read its parent's
 * origin, `postMessage` refuses to deliver without an explicit target, and `'*'` is forbidden
 * because it is readable by whatever document happens to occupy the parent slot. So the frame is
 * TOLD where to post, by the only party that can know.
 *
 * It grants nothing. A value that does not match the real parent simply means the browser drops
 * every message the frame sends, so this cannot be used to redirect anything anywhere.
 *
 * Everything is encoded. `eventId` comes off an organizer's own attribute and an unencoded value
 * could splice extra parameters into a URL we then frame.
 */
export function frameQuery(key: string, sid: string | null): string {
  return (
    "key=" +
    encodeURIComponent(key) +
    (sid ? "&sid=" + encodeURIComponent(sid) : "") +
    // Not encoded, and it is the one value here that does not need to be: an origin as the platform
    // serializes it is scheme, host and optional port, none of which can contain a character that
    // ends a query value. `key` and `sid` are encoded because they are strings we were handed.
    "&parent_origin=" +
    location.origin
  );
}

/** `/embed/checkout?event=<id>&sid=<sid>`, with `sid` omitted entirely when there is none. */
function checkoutUrl(target: CheckoutTarget): string {
  return (
    target.origin +
    "/embed/checkout?event=" +
    encodeURIComponent(target.eventId) +
    (target.intent === "brochure" ? "&intent=brochure" : "") +
    "&" +
    frameQuery(target.key, target.sid)
  );
}

/**
 * The one place this bundle creates an iframe (R37).
 *
 * `allow="payment"` is bare on purpose and must stay that way: its default allowlist is the frame's
 * own origin, which is us. `payment *` would delegate Payment Request to EVERY origin nested inside
 * the frame, which is a wider grant than anything we know Razorpay needs. Task 8's real-Razorpay
 * run is what widens this, with evidence and a NAMED origin list, never a wildcard (R39).
 *
 * `src` is left to the caller: the checkout overlay sets it immediately, while the page surface
 * paints its box first and sets `src` only once the attribution sid has arrived, so the frame it
 * loads is the one that can attribute a booking.
 */
export function newFrame(parent: Element | null, cls: string, title: string): HTMLIFrameElement {
  // Attributes, never a markup string: every value on this element derives from data an organizer
  // supplied.
  const frame = e("iframe", cls, parent) as HTMLIFrameElement;
  frame.title = title;
  frame.setAttribute("allow", "payment");
  return frame;
}

/** `window.open`, never throwing. A sandboxed frame without `allow-popups` throws outright. */
function openWindow(url: string, features?: string): Window | null {
  try {
    return window.open(url, "_blank", features);
  } catch {
    return null;
  }
}

/**
 * Closes whatever is open, restoring the page exactly as it was found.
 *
 * The window references are released HERE and not only in the dialog's own `close`, because the
 * sandboxed route never builds a dialog: checkout is a popup, `activeClose` is null, and a
 * `checkout:close` from that popup would otherwise leave `checkoutWin` pointing at a window we no
 * longer own. Measured consequence, not theory: a replayed `login:open` from that reference opened a
 * SECOND popup, and a replayed `booking:complete` still reached the organizer's analytics. The
 * windows are module state, so their release belongs at module level.
 */
export function closeCheckout(): void {
  checkoutWin = null;
  loginWin = null;
  if (activeClose) activeClose();
}

/**
 * Builds the modal shell: container, scroll lock, focus trap, ESC, backdrop, close button.
 *
 * Everything it takes over is captured first and put back on close, including the previous value of
 * `body.style.overflow` (which is frequently already set by the host's own menu) and the element
 * that had focus. An overlay that leaves a page unable to scroll is worse than one that never
 * opened, because the visitor cannot even read their way out of it.
 */
function shell(): HTMLElement {
  injectStyles();

  const body = document.body;
  const prevOverflow = body.style.overflow;
  const prevFocus = document.activeElement as HTMLElement | null;

  const root = e("div", OWN + " thrivia-o", null);
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-modal", "true");
  root.setAttribute("aria-label", "Thrivia checkout");
  root.tabIndex = -1;

  const onKey = (event: KeyboardEvent): void => {
    if (event.key === "Escape" || event.key === "Esc") close();
  };
  // The container itself, never a child: a click that lands on the frame or the panel is the
  // visitor using the thing, not dismissing it.
  const onClick = (event: MouseEvent): void => {
    if (event.target === root) close();
  };
  // The trap. Capture phase and `focusin` rather than a keydown/Tab dance, because Tab is not the
  // only way out: shift-Tab, a click on the page behind, and a screen reader's own navigation all
  // move focus without a Tab keystroke. Focus entering the cross-origin frame lands on the iframe
  // ELEMENT, which is inside the container, so the visitor can still reach the checkout itself.
  const onFocus = (event: FocusEvent): void => {
    if (!root.contains(event.target as Node)) root.focus();
  };

  let open = true;
  function close(): void {
    if (!open) return;
    open = false;
    if (liveness) clearTimeout(liveness);
    liveness = null;
    activeRoot = null;
    activeClose = null;
    activeFrame = null;
    dropSpinner();
    loginWin = null;
    // The frame it was covering is going out of the document with the root, so there is nothing
    // left to uncover and a stale closure would only unhide a detached element.
    uncover = null;
    document.removeEventListener("keydown", onKey);
    document.removeEventListener("focusin", onFocus, true);
    body.style.overflow = prevOverflow;
    if (root.parentNode) root.parentNode.removeChild(root);
    try {
      if (prevFocus && prevFocus.focus) prevFocus.focus();
    } catch {
      /* the element left the document while we were open */
    }
  }

  root.addEventListener("click", onClick as EventListener);
  document.addEventListener("keydown", onKey);
  document.addEventListener("focusin", onFocus, true);
  body.style.overflow = "hidden";
  body.appendChild(root);

  // Styled inline rather than through the sheet: it is a handful of declarations used in exactly one
  // place, and a rule in the stylesheet costs a selector, its `[class]` padding and a class name on
  // top of the same declarations. Inline also survives an organizer whose CSP blocked our <style>.
  //
  // `width:max-content` IS NOT DECORATION, it is the whole of ruling R50. Without it this control
  // rendered as a WHITE BAR ACROSS THE FULL WIDTH of the viewport, on every framed checkout on every
  // organizer site, not only on a failure path. Measured in Chrome: 1440px wide on a 1440px
  // viewport, computed `left:-8px`, against an intended 39px pill.
  //
  // The cause is a genuine trap rather than a typo. `.thrivia-o` is `display:flex`, and an
  // absolutely positioned child of a FLEX container is sized against its containing block instead of
  // shrink-to-fitting the way the same declarations behave under a block parent, so `right:8px` with
  // `left:auto` did not produce an auto width. The reset's own `width:auto` on `@r~ *` is what it
  // then resolved against. `display:inline-block` does NOT fix it and was measured failing;
  // `max-content` is the declaration that does.
  const x = e("button", OWN, root, "✕");
  x.setAttribute("aria-label", "Close");
  x.style.cssText = "position:absolute;top:8px;right:8px;width:max-content;padding:8px 14px;border-radius:50px;background:#FFF;cursor:pointer";
  x.addEventListener("click", close);

  root.focus();
  activeRoot = root;
  activeClose = close;
  return root;
}

/**
 * A message and a link the visitor clicks themselves, detached (R19).
 *
 * Built entirely from the card's own classes, so it inherits the branding, the measured AA contrast
 * pairs and the approved radii from `styles.ts` and costs NO new CSS: `.thrivia-c` is the card box,
 * `__y` its 16px body, `__t` the 18px heading, `__cap-m` the wrapping 12px `#0B0B0B` line the
 * notify-me capture already uses for its status (18.9:1), and `__a` the coral pill with `#0B0B0B`
 * text (7.22:1, never white on coral). `__t` line-clamps at two lines, which is why the heading is
 * short and the sentence that has to wrap goes in `__cap-m`.
 *
 * Carries `OWN` so `clearOwn`/`swap` can find and remove it: the page surface puts this INSIDE the
 * organizer's own mount element, beside markup that is not ours to touch.
 */
function panel(head: string, detail: string | null, url: string | null): HTMLElement {
  const card = e("div", OWN + " thrivia-c", null);
  const body = e("div", C + "y", card);
  e("p", C + "t", body, head);
  if (detail) e("p", C + "cap-m", body, detail);
  if (url) linkTo(e("a", C + "a", card, CONTINUE), url);
  return card;
}

/**
 * The panel for "the browser would not let us draw here", detached.
 *
 * Exported because the `page` surface renders the same thing for the same reason, and one refusal
 * message on the whole SDK is one thing for support to recognise. See {@link REFUSED} for why the
 * copy reads the way it does.
 */
export function refused(url: string | null): HTMLElement {
  return panel(REFUSED, OWNER + location.origin + DASHBOARD, url);
}

/** Puts a panel in the overlay in place of whatever was there. */
function show(node: HTMLElement): void {
  const root = activeRoot || shell();
  uncover = null;
  dropSpinner();
  if (activeFrame) {
    if (activeFrame.parentNode) activeFrame.parentNode.removeChild(activeFrame);
    activeFrame = null;
  }
  root.appendChild(node);
}

/**
 * Puts `node` in FRONT of a frame instead of in place of it, and returns the undo.
 *
 * The distinction from {@link show} is the whole of ruling R53. `show` is for a frame that is dead:
 * removing it is correct, because there is nothing in it to lose. This is for a frame that is very
 * much alive and merely cannot do the one thing it just asked for, and the two had been sharing a
 * code path. The measured cost of that: a visitor mid-checkout, ticket selection made and contact
 * form half typed, taps "Log in", their browser blocks the popup, and the entire checkout iframe is
 * torn out of the document and replaced with an apology. Every byte of their state goes with it.
 *
 * `display:none` and NOT a removal, because a removed iframe is a destroyed document: its window
 * closes, its form state is gone, and `activeFrame.contentWindow` stops being anything we can post
 * `login:complete` to. A hidden iframe keeps its document, its window and its state, and comes back
 * exactly as it was left.
 *
 * Shared with the page surface, which reaches the same fork for the same reason on a frame that
 * lives inside the organizer's own mount rather than in our dialog. Hence a frame argument and a
 * returned closure rather than anything reading module state: the two callers have different
 * parents and different lifetimes, and only the decision is common.
 */
export function cover(frame: HTMLIFrameElement, node: HTMLElement): () => void {
  const parent = frame.parentNode;
  if (!parent) return function (): void {};
  frame.style.display = "none";
  parent.insertBefore(node, frame);
  return function (): void {
    if (node.parentNode) node.parentNode.removeChild(node);
    frame.style.display = "";
  };
}

/**
 * The popup was refused. The visitor gets a link they can click themselves, which carries their own
 * activation and is never blocked.
 *
 * Deliberately NOT the framing-refusal copy: a blocked popup is a browser setting on the visitor's
 * own machine and there is nothing the site owner can register to fix it. Telling them to go and
 * edit their dashboard would be wrong advice, confidently given.
 */
function degrade(url: string | null): void {
  show(panel(DEGRADED, null, url));
}

/** The frame never spoke: say so, in place, with a way out. */
function presumeDead(): void {
  // `spoke` is the ONLY thing that stands this down. Cancelling the pending timer when a message
  // arrives is not wrong, it is REDUNDANT: a `clearTimeout` issued from a still-running task does
  // cancel a timer whose deadline has already passed, because the queued callback re-checks the
  // active-timer map before it runs (measured in Chromium during review, not reasoned about). Two
  // mechanisms for one decision means neither can be discriminated by a test, which is exactly how
  // this guard first shipped with a mutation surviving. One flag, checked when the timer fires.
  const target = activeTarget;
  if (spoke || !activeRoot || !target) return;
  // NEVER null. `fallback` is null for a bare mount div with no baked anchor and no data yet, and
  // the visitor still has to be given somewhere to go: the checkout URL itself is a real page on
  // our origin that works top-level, which is precisely what the popup path already falls back to.
  // A panel with a message and no link is the silent stall R19 exists to forbid.
  //
  // NO `window.open` HERE ANY MORE. It used to try one first and only fall to a panel when the
  // browser refused, which it always did: this runs on a timer, four seconds after the click, so
  // there is no user activation left and every popup blocker on the market kills it. What that
  // bought was a silent nothing on the common path and, on the uncommon one, a new tab opening by
  // itself on somebody else's website. The panel is in front of the visitor either way, so it is
  // the only place the message can land.
  show(refused(target.fallback || checkoutUrl(target)));
}

/**
 * The login popup, requested by the checkout frame over the bus. R19 applies here too.
 *
 * This is the FALLBACK, not the primary path. The frame opens its own popup while it still holds
 * the visitor's click, because user activation does not cross origins through `postMessage`, so a
 * `window.open` we make on its behalf is frequently blocked. We honour the message anyway: the
 * frame only sends it once its own attempt has already been refused.
 *
 * The query carries our origin as `parent_origin`, which is what lets the popup post
 * `login:complete` back to THIS document (we are the opener on this path, not the frame). Built
 * here from `activeTarget`, never from anything in the message: an origin-validated message is
 * still not a safe source for a URL.
 */
function openLogin(): void {
  if (!activeTarget) return;
  loginPopup(
    activeTarget.origin + "/embed/login?" + frameQuery(activeTarget.key, activeTarget.sid),
    activeFrame,
  );
}

/**
 * Opens the login popup for a frame that asked for one, and handles the refusal WITHOUT costing that
 * frame its state.
 *
 * A BLOCKED POPUP HERE IS THE EXPECTED PATH, NOT AN EXOTIC ONE, and that is why this is written the
 * way it is. The frame only sends `login:open` once its OWN `window.open` was already refused, and
 * ours is made from a message handler with no user activation behind it, so the browser refuses it
 * for the same reason and more reliably. Any handling that treats the refusal as a rare accident is
 * therefore handling the common case, and this one used to end in `degrade`, which tore the live
 * checkout out of the document (see {@link cover}).
 *
 * So: the frame is covered, never removed. The visitor gets the link they can click themselves,
 * which carries their own activation and is never blocked, and clicking it uncovers the checkout
 * they were half way through so it is still there when they come back from logging in. The panel
 * deliberately does NOT use the framing-refusal copy: a blocked popup is a setting on the visitor's
 * own machine and there is nothing the site owner can register to fix it.
 *
 * `frame` is null on the sandboxed route, where checkout is itself a popup and there is no frame to
 * protect; that case still degrades in place, which is all it ever had.
 *
 * Exported for the page surface, whose mount frames ask for exactly the same thing.
 */
export function loginPopup(url: string, frame: HTMLIFrameElement | null): Window | null {
  loginWin = openWindow(url, POPUP);
  if (loginWin) return loginWin;
  if (!frame) {
    degrade(url);
    return null;
  }
  const node = panel(DEGRADED, null, url);
  const undo = cover(frame, node);
  uncover = undo;
  // The visitor's own click on the link is the signal that they are dealing with it themselves, so
  // the checkout comes back out from under the panel now rather than when the login finishes: they
  // may abandon the login, and a hidden checkout would then be a dialog with nothing in it.
  node.addEventListener("click", function (): void {
    if (uncover === undo) uncover = null;
    undo();
  });
  return null;
}

/**
 * Moves the TOP document to JioPay's hosted page, which refuses to render inside a frame.
 *
 * The frame is not trusted blindly: this navigates the organizer's own page, so the URL is
 * re-checked here, as a string with no URL parsing to disagree with the browser. https, JioPay's
 * host or a subdomain of it, then a port, path, query, fragment or the end. Userinfo (`@`) and a
 * backslash cannot appear before the first of those, so neither can smuggle another host in.
 *
 * JioPay also checks the Referer and accepts only https://www.thrivia.io/, so the page is reached
 * through a static bounce on the app origin (public/pay/jiopay.html) that re-checks the URL and
 * replaces itself with it; assigning JioPay directly would send the organizer's domain as Referer.
 */
const JIOPAY_URL = /^https:\/\/([a-z0-9-]+\.)*jiopay\.co\.in(:\d+)?([/?#]|$)/i;

/**
 * The one bus subscription for the checkout lifecycle.
 *
 * Registered once, not once per open, or a visitor who opened checkout three times would see three
 * `thrivia:booking:complete` events for one booking.
 */
function handle(type: BusType, payload: BusPayload, source: unknown): void {
  // Origin was checked by the bus; this checks WHICH app-origin document. It also closes the
  // post-close window on BOTH routes: closing releases every window reference we were holding (the
  // framed one in the dialog's `close`, the popup in `closeCheckout`), so a late message from a
  // checkout the visitor has already finished with cannot resurrect a dialog, open a second popup,
  // or push another booking into their analytics.
  if (!known(source)) return;

  // Any valid message proves the frame loaded OUR document rather than a 404 page. It has to come
  // before the type switch: a frame that only ever posts `error` is still a frame that is alive.
  // The pending timer is deliberately NOT cancelled here; see `presumeDead`.
  spoke = true;
  dropSpinner();

  if (type === "booking:complete") emitBooking(payload);
  else if (type === "checkout:close") closeCheckout();
  else if (type === "login:open") openLogin();
  else if (type === "checkout:handoff" && activeTarget && typeof payload.url == "string" && JIOPAY_URL.test(payload.url))
    location.assign(activeTarget.origin + "/pay/jiopay.html?to=" + encodeURIComponent(payload.url));
  // The popup posts to its opener, which is us; the checkout frame is the one that needs to know.
  // The degraded panel comes down first: the login it was offering has happened, so leaving it in
  // front of a checkout that is now logged in would hide the very thing the visitor came back for.
  else if (type === "login:complete") {
    // The login is finished and the popup closes itself, so the reference is released HERE and not
    // only when the whole checkout closes. `known()` matches on window identity, and a reference we
    // keep after the document behind it is gone is exactly the stale-source readmission the null
    // check in `known` exists to prevent - held open, on this one path, for the rest of the
    // checkout's life.
    loginWin = null;
    if (uncover) {
      uncover();
      uncover = null;
    }
    post(checkoutWindow(), "login:complete", payload);
  }
}

/**
 * Opens checkout for one mount. Returns true when a checkout surface actually reached the visitor.
 *
 * The return value is what the `checkout_open` beacon is conditioned on: a popup the browser
 * refused is not an open checkout, and counting it would tell a creator their embed converts badly
 * when in fact it never rendered.
 *
 * SYNCHRONOUS from click to `window.open`. Do not make this function async, do not await anything
 * in it, and do not move the platform decision into it: `sandboxed` is computed at boot precisely
 * so this path has nothing to wait for.
 */
export function openCheckout(target: CheckoutTarget): boolean {
  if (!subscribed) {
    subscribed = true;
    onBus(handle);
  }

  // One at a time, and before `activeTarget` is reassigned: closing releases the previous one.
  closeCheckout();
  const url = checkoutUrl(target);
  activeTarget = target;
  checkoutWin = null;
  spoke = false;

  if (target.sandboxed) {
    checkoutWin = openWindow(url, POPUP);
    if (checkoutWin) return true;
    // R19: the popup was refused, so the visitor gets a link instead of nothing at all.
    degrade(target.fallback || url);
    return false;
  }

  const root = shell();
  const frame = newFrame(root, "thrivia-o__f", "Thrivia checkout");
  frame.src = url;
  activeFrame = frame;
  // Something to look at while the frame loads. Layered above the iframe, dropped on its first
  // message; `animate` is the Web Animations API so no @keyframes (whose name would not carry the
  // `.thrivia-` prefix) is needed, and a host without it just gets a static ring.
  spinner = e("div", OWN + " thrivia-o__s", root);
  if (spinner.animate)
    spinner.animate([{ transform: "rotate(0)" }, { transform: "rotate(1turn)" }], {
      duration: 800,
      iterations: Infinity,
    });
  liveness = setTimeout(presumeDead, FRAME_TIMEOUT_MS);
  return true;
}
